import * as React from "react";
import { createFileRoute, notFound, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellOff, BellRing, Hash } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { resolveCanonicalTagSlug } from "@/lib/tag-resolve.functions";
import { EmptyState } from "@/components/empty-state";
import { StoryCard, StoryCardSkeleton } from "@/components/story-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MembersOnlyGate } from "@/components/members-only-gate";
import { useAuth } from "@/lib/auth-context";
import { fetchContentTypes } from "@/lib/queries";
import {
  fetchMyTagPreferences,
  fetchTagBySlug,
  fetchTagPublications,
  setTagFollow,
  setTagMute,
  tagLabel,
  type TagSort,
} from "@/lib/tags";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  sort: z.enum(["trending", "newest", "most_read", "most_liked"]).optional(),
  type: z.string().optional(),
  lang: z.string().optional(),
  length: z.enum(["short", "medium", "long"]).optional(),
  since: z.enum(["week", "month", "year"]).optional(),
});

export const Route = createFileRoute("/tag/$slug")({
  validateSearch: searchSchema,
  // Aliases and merged duplicates are resolved on the server before render, so
  // every spelling redirects to the one canonical hashtag URL.
  beforeLoad: async ({ params, search }) => {
    const resolved = await resolveCanonicalTagSlug({ data: { slug: params.slug } });
    if (!resolved.found) throw notFound();
    if (resolved.slug && resolved.slug !== params.slug) {
      throw redirect({
        to: "/tag/$slug",
        params: { slug: resolved.slug },
        search,
        replace: true,
        statusCode: 301,
      });
    }
  },
  head: ({ params }) => {
    const label = `#${params.slug.replace(/-/g, "")}`;
    return {
      meta: [
        { title: `${label} — hashtag on Quibblies` },
        {
          name: "description",
          content: `Browse approved Quibblies publications tagged ${label}. Sort by trending, newest, most read or most liked, and filter by publication type, language and length.`,
        },
        { property: "og:title", content: `${label} — hashtag on Quibblies` },
        {
          property: "og:description",
          content: `Approved writing tagged ${label} on Quibblies.`,
        },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: TagPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
      <EmptyState
        icon={Hash}
        title="No such hashtag"
        description="This hashtag isn't in the approved registry — it may have been archived, blocked or never existed."
      />
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
      <EmptyState
        icon={Hash}
        title="Couldn't load this hashtag"
        description="Something went wrong fetching this hashtag page. Please refresh and try again."
      />
    </div>
  ),
});

const SORTS: { value: TagSort; label: string }[] = [
  { value: "trending", label: "Trending" },
  { value: "newest", label: "Newest" },
  { value: "most_read", label: "Most read" },
  { value: "most_liked", label: "Most liked" },
];

const LENGTHS = [
  { value: "short", label: "Short" },
  { value: "medium", label: "Medium" },
  { value: "long", label: "Long" },
] as const;

const DATES = [
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
] as const;

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
] as const;

function FilterRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border border-border/70 px-3 py-1 text-xs transition-smooth hover:border-primary/60 hover:text-foreground",
        active ? "border-primary/60 bg-accent text-foreground" : "text-muted-foreground",
      )}
    >
      {children}
    </button>
  );
}

function TagPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/tag/$slug" });
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const sort = search.sort ?? "trending";

  const tag = useQuery({ queryKey: ["tag", slug], queryFn: () => fetchTagBySlug(slug) });


  const contentTypes = useQuery({ queryKey: ["content-types"], queryFn: fetchContentTypes });

  const publications = useQuery({
    queryKey: ["tag-publications", tag.data?.id, search],
    queryFn: () =>
      fetchTagPublications(tag.data!.id, {
        sort,
        ...(search.type ? { contentTypeSlug: search.type } : {}),
        ...(search.lang ? { language: search.lang } : {}),
        ...(search.length ? { length: search.length } : {}),
        ...(search.since ? { since: search.since } : {}),
      }),
    enabled: Boolean(tag.data?.id),
  });

  const prefs = useQuery({
    queryKey: ["tag-preferences", user?.id],
    queryFn: () => fetchMyTagPreferences(user!.id),
    enabled: Boolean(user),
  });

  const following = Boolean(tag.data && prefs.data?.followedIds.has(tag.data.id));
  const muted = Boolean(tag.data && prefs.data?.mutedIds.has(tag.data.id));

  const toggleFollow = useMutation({
    mutationFn: () => setTagFollow(user!.id, tag.data!.id, !following),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tag-preferences"] });
      toast.success(following ? "Tag unfollowed" : "Following this tag — kept private to you.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleMute = useMutation({
    mutationFn: () => setTagMute(user!.id, tag.data!.id, !muted),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tag-preferences"] });
      toast.success(muted ? "Tag unmuted" : "Tag muted — hidden from your recommendations.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function update(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <MembersOnlyGate
          title="Hashtags are for members"
          description="Sign in to browse writing tagged this way. Publications are visible to signed-in members only."
        />
      </div>
    );
  }

  if (tag.isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-12 sm:px-6">
        <Skeleton className="h-12 w-48 rounded-full" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <StoryCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (!tag.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          icon={Hash}
          title="Hashtag not found"
          description="This hashtag isn't approved on Quibblies, or it has been archived."
        />
      </div>
    );
  }

  const stories = publications.data ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl text-foreground sm:text-5xl">
            {tagLabel(tag.data)}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {tag.data.description ??
              `Approved publications tagged ${tagLabel(tag.data)}.`}{" "}
            {tag.data.usage_count ?? 0} publications use this hashtag.
          </p>
        </div>

        {user ? (
          <div className="flex gap-2">
            <Button
              variant={following ? "hero" : "soft"}
              size="sm"
              disabled={toggleFollow.isPending}
              onClick={() => toggleFollow.mutate()}
            >
              <BellRing className="h-4 w-4" aria-hidden="true" />
              {following ? "Following" : "Follow tag"}
            </Button>
            <Button
              variant={muted ? "hero" : "soft"}
              size="sm"
              disabled={toggleMute.isPending}
              onClick={() => toggleMute.mutate()}
            >
              <BellOff className="h-4 w-4" aria-hidden="true" />
              {muted ? "Muted" : "Mute tag"}
            </Button>
          </div>
        ) : null}
      </header>

      <p className="mt-3 text-xs text-muted-foreground">
        Tags you follow or mute stay private to your account.
      </p>

      <div className="mt-8 space-y-3 rounded-3xl border border-border/70 bg-surface/40 p-4">
        <FilterRow label="Sort">
          {SORTS.map((option) => (
            <Chip
              key={option.value}
              active={sort === option.value}
              onClick={() => update({ sort: option.value })}
            >
              {option.label}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Type">
          <Chip active={!search.type} onClick={() => update({ type: undefined })}>
            All
          </Chip>
          {(contentTypes.data ?? []).map((type) => (
            <Chip
              key={type.id}
              active={search.type === type.slug}
              onClick={() => update({ type: type.slug })}
            >
              {type.name}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Language">
          <Chip active={!search.lang} onClick={() => update({ lang: undefined })}>
            Any
          </Chip>
          {LANGUAGES.map((language) => (
            <Chip
              key={language.value}
              active={search.lang === language.value}
              onClick={() => update({ lang: language.value })}
            >
              {language.label}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Length">
          <Chip active={!search.length} onClick={() => update({ length: undefined })}>
            Any
          </Chip>
          {LENGTHS.map((option) => (
            <Chip
              key={option.value}
              active={search.length === option.value}
              onClick={() => update({ length: option.value })}
            >
              {option.label}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="Date">
          <Chip active={!search.since} onClick={() => update({ since: undefined })}>
            All time
          </Chip>
          {DATES.map((option) => (
            <Chip
              key={option.value}
              active={search.since === option.value}
              onClick={() => update({ since: option.value })}
            >
              {option.label}
            </Chip>
          ))}
        </FilterRow>
      </div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {publications.isLoading
          ? Array.from({ length: 6 }).map((_, index) => <StoryCardSkeleton key={index} />)
          : stories.map((story) => <StoryCard key={story.id} story={story} />)}
      </div>

      {!publications.isLoading && stories.length === 0 ? (
        <EmptyState
          className="mt-10"
          icon={Hash}
          title="Nothing here yet"
          description={`No approved publications carry ${tagLabel(tag.data)} with these filters.`}
        />
      ) : null}
    </div>
  );
}
